import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Card, CardContent } from './ui/card';
import { Trophy, MapPin } from 'lucide-react';
import { userAPI } from '../services/api';
import { Skeleton } from './ui/skeleton';

const Sidebar = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        // First try to get from localStorage for immediate display
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        if (storedUser && storedUser.username) {
          setUser(storedUser);
        }

        // Then fetch fresh data from API
        const response = await userAPI.getMyProfile();
        if (response.data) {
          setUser(response.data);
          // Update localStorage with fresh data
          localStorage.setItem('user', JSON.stringify(response.data));
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
        // Fall back to localStorage if API fails
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        if (storedUser) {
          setUser(storedUser);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, []);

  if (loading && !user) {
    return (
      <div className="space-y-4" data-testid="sidebar-skeleton">
        <Card className="overflow-hidden shadow-sm">
          <Skeleton className="h-16 w-full rounded-none" />
          <CardContent className="pt-0 pb-4">
            <div className="flex flex-col items-center -mt-10">
              <Skeleton className="w-20 h-20 rounded-full border-4 border-background" />
              <Skeleton className="h-4 w-24 mt-3" />
              <Skeleton className="h-3 w-20 mt-2" />
              <Skeleton className="h-3 w-32 mt-2" />
            </div>
            <div className="border-t mt-4 pt-4">
              <div className="flex justify-around text-center">
                <div className="flex flex-col items-center gap-1">
                  <Skeleton className="h-5 w-8" />
                  <Skeleton className="h-3 w-16" />
                </div>
                <div className="flex flex-col items-center gap-1">
                  <Skeleton className="h-5 w-8" />
                  <Skeleton className="h-3 w-14" />
                </div>
                <div className="flex flex-col items-center gap-1">
                  <Skeleton className="h-5 w-8" />
                  <Skeleton className="h-3 w-14" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  // Get display values with fallbacks
  const displayName = user.name || 'User';
  const displayUsername = user.username || 'user';
  const displayPersona = user.persona || 'Member';
  const displaySport = user.sports?.[0] || user.sport || '';
  const displayLocation = user.location || '';
  const connections = user.connections || 0;
  const followers = user.followers || 0;
  const following = user.following || 0;
  const stats = user.stats || {};

  return (
    <div className="space-y-4">
      {/* Profile Card */}
      <Card className="overflow-hidden shadow-sm hover:shadow-md transition-all">
        {/* Cover image or gradient */}
        <div 
          className="h-16 bg-cover bg-center"
          style={{
            backgroundImage: user.coverImage 
              ? `url(${user.coverImage})`
              : 'linear-gradient(135deg, #8854C8 0%, #6B3FA0 100%)'
          }}
        ></div>
        <CardContent className="pt-0 pb-4">
          <div className="flex flex-col items-center -mt-10">
            <Link to={`/profile/${displayUsername}`}>
              <Avatar className="w-20 h-20 border-4 border-background shadow-lg hover:scale-105 transition-transform cursor-pointer">
                <AvatarImage src={user.profileImage} alt={displayName} />
                <AvatarFallback className="text-xl bg-gradient-to-br from-primary to-purple-600 text-white">
                  {displayName[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </Link>
            <Link to={`/profile/${displayUsername}`} className="mt-3 text-center hover:underline">
              <h3 className="font-semibold text-foreground">{displayName}</h3>
              <p className="text-sm text-muted-foreground">@{displayUsername}</p>
            </Link>
            <div className="flex items-center mt-2 text-sm text-muted-foreground">
              <Trophy className="w-4 h-4 mr-1 text-primary" />
              <span className="capitalize">{displayPersona}{displaySport ? ` • ${displaySport}` : ''}</span>
            </div>
            {displayLocation && (
              <div className="flex items-center mt-1 text-sm text-muted-foreground">
                <MapPin className="w-4 h-4 mr-1" />
                <span>{displayLocation}</span>
              </div>
            )}
          </div>

          <div className="border-t mt-4 pt-4">
            <div className="flex justify-around text-center">
              <div>
                <p className="font-bold text-lg text-primary">{connections}</p>
                <p className="text-xs text-muted-foreground">Connections</p>
              </div>
              <div>
                <p className="font-bold text-lg text-primary">{followers}</p>
                <p className="text-xs text-muted-foreground">Followers</p>
              </div>
              <div>
                <p className="font-bold text-lg text-primary">{following}</p>
                <p className="text-xs text-muted-foreground">Following</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Stats for Athletes */}
      {displayPersona.toLowerCase() === 'athlete' && stats && Object.keys(stats).length > 0 && (
        <Card className="shadow-sm hover:shadow-md transition-all">
          <CardContent className="pt-6">
            <h4 className="font-semibold text-foreground mb-4 flex items-center">
              <Trophy className="w-5 h-5 mr-2 text-primary" />
              Performance Stats
            </h4>
            <div className="space-y-3">
              {stats.competitions !== undefined && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Competitions</span>
                  <span className="font-semibold text-primary">{stats.competitions}</span>
                </div>
              )}
              {stats.wins !== undefined && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Wins</span>
                  <span className="font-semibold text-primary">{stats.wins}</span>
                </div>
              )}
              {stats.podiums !== undefined && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Podiums</span>
                  <span className="font-semibold text-primary">{stats.podiums}</span>
                </div>
              )}
              {stats.personalBests && stats.personalBests.length > 0 && (
                <div className="border-t pt-3 mt-3">
                  <p className="text-xs font-semibold text-foreground mb-2">Personal Bests</p>
                  {stats.personalBests.map((pb, index) => (
                    <div key={index} className="flex justify-between items-center text-sm mb-1">
                      <span className="text-muted-foreground">{pb.event}</span>
                      <span className="font-semibold text-primary">{pb.time || pb.value}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Sidebar;
